import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formErrorMessage, setFormErrorMessage] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9nin8kit35ii3jdlrr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, String(v)])
        ).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormErrorMessage(msg);
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch {
      setFormErrorMessage('网络错误，请检查网络后重试');
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const faqList = [
    {
      q: '6080新视觉影院是什么？',
      a: '6080新视觉影院是一个专业的免费高清电影在线观看平台，为用户提供最新电影、电视剧全集的高速流畅播放服务。我们的网站采用先进的视频加速技术，确保影片秒开不缓冲，真正实现极速观影体验。',
    },
    {
      q: '6080新视觉影院的影片资源是否免费？',
      a: '是的，6080新视觉影院所有影片资源均为免费提供。用户无需注册、无需付费即可畅享海量高清影视内容。我们致力于为每一位影迷提供优质的免费观影服务。',
    },
    {
      q: '6080新视觉影院支持哪些设备观看？',
      a: '6080新视觉影院支持电脑、手机、平板等多种设备在线观看。网站采用响应式设计，自动适配各种屏幕尺寸，无论您使用何种设备，都能获得流畅舒适的观影体验。',
    },
    {
      q: '6080新视觉影院的影片画质如何？',
      a: '我们提供最高1080P全高清画质，部分热门影片支持4K超清播放。6080新视觉影院始终将观影画质放在首位，确保每一位用户都能享受影院级的视觉盛宴。',
    },
    {
      q: '6080新视觉影院多久更新一次内容？',
      a: '6080新视觉影院的影片资源每日更新，第一时间上线最新上映的热门电影和电视剧。我们的内容团队实时追踪全球影视动态，确保用户始终能看到最新、最热的影视作品。',
    },
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  const breadcrumbListSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
        item: typeof window !== 'undefined' ? window.location.origin + '/about' : '/about',
      },
    ],
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '6080新视觉影院',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '6080新视觉影院 - 免费高清电影在线观看平台，提供最新电影电视剧全集不卡顿流畅播放服务。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@6080xinshijue.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbListSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="w-full px-4 md:px-6 py-8 md:py-12">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
                <Link to="/" className="hover:text-accent-600 transition-colors">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium">关于我们</span>
              </nav>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-[var(--font-heading)]">
                关于6080新视觉影院
              </h1>
            </div>
          </div>

          <section className="py-10 md:py-14">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <article>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5 font-[var(--font-heading)]">
                    品牌介绍
                  </h2>
                  <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed space-y-4">
                    <p>
                      <strong>6080新视觉影院</strong>诞生于对高品质在线观影体验的执着追求。我们的团队由一群热爱电影的资深影迷和技术专家组成，致力于打造全网最流畅、最高清的免费电影在线观看平台。
                    </p>
                    <p>
                      自创立以来，6080新视觉影院始终坚持以用户体验为核心，持续投入技术研发和内容建设。我们采用业界领先的P2P加速技术和智能CDN分发系统，确保每一位用户无论身处何地，都能享受到<strong>极速秒开、不卡顿、不缓冲</strong>的流畅观影体验。
                    </p>
                    <p>
                      在内容方面，6080新视觉影院收录了超过十万部电影电视剧资源，涵盖<strong>动作片、喜剧片、爱情片、科幻片、恐怖片、剧情片、悬疑片、动画片</strong>等全部热门分类。从经典老片到最新院线大片，从国产精品到海外佳作，应有尽有。我们的内容团队7×24小时不间断更新，确保用户始终能第一时间看到最新上线的影片。
                    </p>
                    <p>
                      6080新视觉影院深知用户对画质的追求。我们提供最高1080P全高清画质，热门影片更支持4K超清播放。每一帧画面都经过精心优化，在保证流畅播放的同时，最大程度呈现影片的细腻质感和丰富色彩。
                    </p>
                    <p>
                      展望未来，6080新视觉影院将继续深耕影视领域，不断提升技术实力和内容质量。我们相信，观影不仅仅是一种娱乐方式，更是一种生活方式。6080新视觉影院将陪伴每一位影迷，共同探索光影世界的无限魅力。
                    </p>
                  </div>
                </article>

                <div className="mt-12 md:mt-16">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-[var(--font-heading)]">
                    用户口碑
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      {
                        name: '电影爱好者小陈',
                        text: '6080新视觉影院是我用过最流畅的免费电影网站！画质清晰，播放速度快，而且真的没有广告打扰，观影体验一级棒！',
                        rating: 5,
                      },
                      {
                        name: '追剧达人Linda',
                        text: '每天下班最大的享受就是在6080新视觉影院追剧，资源更新特别快，新剧上线第二天就能看到，强烈推荐！',
                        rating: 5,
                      },
                      {
                        name: '影视评论人老王',
                        text: '作为一个看了十几年电影的老影迷，6080新视觉影院的片库丰富程度让我非常惊喜。很多经典老片都能找到高清资源。',
                        rating: 5,
                      },
                    ].map((review, idx) => (
                      <div key={idx} className="bg-background-100 rounded-lg p-5">
                        <div className="flex items-center gap-1 mb-3">
                          {Array.from({ length: review.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-amber-500 text-sm"></i>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                          "{review.text}"
                        </p>
                        <p className="text-xs font-medium text-foreground-900">{review.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-12 md:mt-16">
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 font-[var(--font-heading)]">
                    联系我们
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <div className="bg-background-100 rounded-lg p-6 space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg">
                            <i className="ri-mail-line text-lg text-accent-600"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-500">电子邮箱</p>
                            <p className="text-sm font-medium text-foreground-900">service@6080xinshijue.com</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg">
                            <i className="ri-customer-service-2-line text-lg text-accent-600"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-500">客服热线</p>
                            <p className="text-sm font-medium text-foreground-900">400-888-6080</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg">
                            <i className="ri-time-line text-lg text-accent-600"></i>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-500">工作时间</p>
                            <p className="text-sm font-medium text-foreground-900">周一至周日 9:00 - 22:00</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <form
                        id="contact-form"
                        data-readdy-form=""
                        onSubmit={handleSubmit}
                        className="bg-background-100 rounded-lg p-6 space-y-4"
                      >
                        <h3 className="font-semibold text-foreground-950 mb-2">发送消息</h3>
                        {formStatus === 'success' && (
                          <div className="bg-green-50 text-green-700 text-sm px-4 py-2.5 rounded-lg">
                            <i className="ri-checkbox-circle-line mr-1.5"></i>消息发送成功！感谢您的联系，我们会尽快回复。
                          </div>
                        )}
                        {formStatus === 'error' && (
                          <div className="bg-red-50 text-red-600 text-sm px-4 py-2.5 rounded-lg">
                            <i className="ri-error-warning-line mr-1.5"></i>{formErrorMessage}
                          </div>
                        )}
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="您的姓名"
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="您的邮箱"
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors"
                            required
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            placeholder="请输入您想说的话..."
                            rows={4}
                            maxLength={500}
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400 transition-colors resize-none"
                            required
                          />
                        </div>
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                          className="form-honeypot"
                        />
                        <button
                          type="submit"
                          className="w-full py-2.5 bg-accent-500 text-background-50 rounded-lg text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          发送消息
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-100">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center font-[var(--font-heading)]">
                  常见问题
                </h2>
                <div className="space-y-3">
                  {faqList.map((item, idx) => (
                    <details key={idx} className="bg-background-50 rounded-lg group">
                      <summary className="px-5 py-4 text-sm font-medium text-foreground-900 cursor-pointer list-none flex items-center justify-between">
                        {item.q}
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {item.a}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}